<template>
	<StandardModal
		open
		medium
		:title="$t('chat_readreceipts_title')"
		:tabOrder="[
			{
				css: '.pronto-input input',
				clickOnEnter: true,
				Enter: () => {
					focusInput()
				},
			},
			{ css: '.tabs .tab-item:nth-child(1)', clickOnEnter: true },
			{ css: '.tabs .tab-item:nth-child(2)', clickOnEnter: true },
			{ css: '.refresh-icon', clickOnEnter: true },
		]"
		@cancel="$emit('cancel')"
		ref="modal"
	>
		<div class="seenby-modal">
			<div class="filtering">
				<Input
					:placeholder="$t('admin_people_search_action')"
					class="search"
					v-model="query"
					:icon="SearchIcon"
					ref="receiptSearch"
				/>
			</div>

			<Tabs
				v-if="!query"
				stretch
				:tabs="tabs"
				:defaultSelected="0"
				class="seenby-tabs"
				selectedcolor="#2c2d31"
				@change="onChangeTab"
			>
				<template v-slot="{ tab }">
					<div
						v-text="$t(`readreceipts_${tab.path}_count`, [getTabCount(tab)])"
					/>
				</template>
			</Tabs>

			<Table
				:columns="columns"
				:rows="users"
				:loadingMore="loadingMore"
				:loadingOverlay="loading"
				infinite
				hideHeader
				@load-more="loadMore"
				@row-click="openUserProfile"
				loadMoreTrigger="50px"
				ref="table"
				:height="query ? '462px' : '402px'"
				clickable
			>
				<template #cell-user="{ data }">
					<div class="user-item" :class="{ 'unseen-row': !data.read && query }">
						<div class="persona">
							<ProfilePic :user="data.user" size="40px" />
							<div class="name" v-text="data.user.fullname" />
						</div>
						<div v-if="!data.read && query" class="status">
							{{ $t(`readreceipts_not_seen_label`) }}
						</div>
					</div>
				</template>

				<template #empty>
					<div v-if="!query && users.length === 0" class="empty-state">
						{{
							users.length === 0 && !currentTab
								? $t('readreceipts_seen_empty_state')
								: $t('readreceipts_not_seen_empty_state')
						}}
					</div>
					<NoSearchResults v-if="query" :query="query" />
				</template>
			</Table>
		</div>
		<template #footer-left>
			<Button link @click="refresh">
				<ArrowsRotateIcon type="regular" class="refresh-icon" />
				{{ $t('common_refresh') }}
			</Button>
		</template>
		<template #footer-right>
			<Button primary @click="$emit('cancel')">
				{{ $t('common_button_done') }}
			</Button>
		</template>
	</StandardModal>
</template>

<script>
import {
	StandardModal,
	Button,
	Input,
	Tabs,
	ProfilePic,
	Table,
	NoSearchResults,
} from '@/components/common'
import { SearchIcon, ArrowsRotateIcon } from '@/icons'
import { get } from '@/api/rest'
import debounce from 'debounce'
import { sleep, showUserModal } from '@/helpers/utils'
import { markRaw } from 'vue'

export const PAGE_SIZE = 30

function mapUser({ user, read }) {
	return {
		id: user.id,
		user,
		read,
	}
}

export default {
	name: 'ReadReceiptsModal',
	components: {
		StandardModal,
		Button,
		Input,
		Tabs,
		ProfilePic,
		ArrowsRotateIcon,
		Table,
		NoSearchResults,
	},
	props: {
		receiptsUrl: String,
		countsUrl: String,
		bubbleId: [Number, String],
	},
	data() {
		return {
			query: null,
			SearchIcon: markRaw(SearchIcon),
			tabs: [
				{ id: 0, path: 'seen' },
				{ id: 1, path: 'not_seen' },
			],
			users: [],
			currentTab: null,
			columns: [{ field: 'user' }],
			loadingMore: false,
			loading: false,
			not_seenCount: 0,
			seenCount: 0,
		}
	},

	emits: ['cancel'],

	watch: {
		query() {
			if (this.query) {
				this.loading = true
				this.fetchUsersDebounced()
			} else {
				this.loading = true
				this.fetchCounts()
				this.fetchUsers()
			}
		},
	},

	created() {
		this.fetchUsersDebounced = debounce(this.fetchUsers.bind(this), 500)
	},

	mounted() {
		this.currentTab = 0
		this.fetchUsers()
		this.fetchCounts()
	},

	methods: {
		async focusInput() {
			this.accessibilityMode = false
			await sleep(100)
			this.$nextTick(() => {
				const input = this.$refs.receiptSearch
				input.focus()
			})
		},

		onChangeTab({ id }) {
			this.currentTab = id
			this.fetchUsers()
		},

		tabHasCount() {
			return this.users.length
		},

		async loadMore() {
			if (!this.apiResponse?.next) return
			try {
				this.loadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.users = this.users.concat(this.apiResponse.data.map(mapUser))
			} catch (e) {
				console.error(e)
				this.$alert.error(this.$t('common_api_error_save'))
			}
			this.loadingMore = false
		},

		getTabCount(tab) {
			return tab.path === 'seen' ? this.seenCount : this.not_seenCount
		},

		async fetchUsers() {
			this.loading = true

			try {
				const params = { 'page[size]': PAGE_SIZE }

				if (this.query) {
					params['filter[search]'] = this.query
				}

				if (!this.query) {
					params['filter[read]'] = this.currentTab ? false : true
				}

				this.apiResponse = await get(this.receiptsUrl, params)
				this.users = this.apiResponse.data.map(mapUser)
				this.loading = false
				this.$refs.table?.scrollToTop()
			} catch (e) {
				console.error(e)
				this.loading = false
				this.$refs.modal.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async fetchCounts() {
			try {
				this.seenCount = 0
				this.not_seenCount = 0

				const { data } = await get(this.countsUrl)

				this.seenCount = data.total_read
				this.not_seenCount = data.total_unread
			} catch (e) {
				console.error(e)
				this.$refs.modal.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async refresh() {
			if (!this.query) {
				this.fetchCounts()
			}

			this.fetchUsers()
		},

		openUserProfile(row) {
			showUserModal(row.user.id, this.bubbleId)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.seenby-modal {
	display: flex;
	flex-direction: column;

	.filtering {
		border-bottom: 1px solid var(--neutral-200);
		padding: 14px 22px;
	}

	.seenby-tabs {
		.text {
			font-size: 17px;
		}
		:deep(.tabs) {
			padding: 0;
			.tab-item {
				padding: 0;
				height: 60px;
			}
		}
	}

	.unseen-row {
		opacity: 0.6;
	}

	.user-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 8px 0px;
		font-family: map-get($fonts, 400);
		font-size: 16px;

		.persona {
			display: flex;
			align-items: center;
			min-width: 0;

			.name {
				margin-left: 12px;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				max-width: 400px;
			}
		}
		.status {
			color: var(--neutral-600);
			font-style: italic;
		}
	}

	:deep(.tabs-container) {
		.tab-item {
			padding: 31px 21px 31px 21px;
			font-family: Avenir-Heavy;
			font-size: 17px;
		}
	}

	.empty-state {
		font-family: map-get($fonts, 400);
		color: var(--neutral-500);
		width: 258px;
		text-align: center;
	}
}

:deep(.refresh-icon) {
	margin-right: 8px;
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.seenby-modal {
		.filtering {
			border-bottom: 1px solid var(--neutral-300);
		}
	}
}
</style>
